import type { ExecuteToolResponse } from "@arcadeai/arcadejs/resources/index.mjs";
import { create } from "zustand";
import type { Parameter, Tool } from "@/app/tools/types";

type ExecutionErrorResponse = {
  status: "error";
  message: string;
  name?: string;
  duration?: number;
};

export type PlaygroundResponse = ExecuteToolResponse | ExecutionErrorResponse;

export type AuthStatus = "pending" | "completed" | "failed" | null;

type PlaygroundState = {
  // Tool selection
  selectedTool: Tool | null;
  parameters: Parameter[];
  toolInputByFqn: Record<string, Record<string, unknown>>;
  setSelectedTool: (tool: Tool) => void;
  setToolInput: (toolFqn: string, values: Record<string, unknown>) => void;

  // Execution state
  isLoading: boolean;
  response: PlaygroundResponse | null;
  setLoading: (isLoading: boolean) => void;
  setResponse: (response: PlaygroundResponse | null) => void;

  // Auth state
  authUrl: string | null;
  authStatus: AuthStatus;
  authError: string | null;
  setAuthState: (state: {
    status: AuthStatus;
    url?: string | null;
    error?: string | null;
  }) => void;
};

const initialState = {
  selectedTool: null,
  parameters: [],
  toolInputByFqn: {},
  isLoading: false,
  response: null,
  authUrl: null,
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  authStatus: null as AuthStatus,
  authError: null,
};

export const usePlaygroundStore = create<PlaygroundState>((set, get) => ({
  ...initialState,

  setSelectedTool: (tool: Tool) => {
    if (!tool) {
      set(initialState);
      return;
    }

    const parameters = tool.input?.parameters ?? [];
    const { toolInputByFqn } = get();
    set({
      ...initialState,
      toolInputByFqn,
      selectedTool: tool,
      parameters,
    });
  },

  setToolInput: (toolFqn, values) => {
    set((state) => ({
      toolInputByFqn: {
        ...state.toolInputByFqn,
        [toolFqn]: values,
      },
    }));
  },

  setLoading: (isLoading) => set({ isLoading }),

  setResponse: (response) => set({ response }),

  setAuthState: ({ status, url, error }) =>
    set({
      authStatus: status,
      authUrl: url ?? null,
      authError: error ?? null,
    }),
}));
